import DOMPurify from "dompurify";
import { isEmpty } from "@utils/var.check";
class HtmlSanitizer {
  private globalAttributes: string[] = [];
  private allowedAttributesMapping: Record<string, string[]> = {};
  private allowedAttributeValuesMappingFunction: Record<
    string,
    Record<string, (arg: string) => boolean>
  > = {};

  constructor() {
    this.init();
  }

  private init() {
    DOMPurify.addHook("uponSanitizeAttribute", (node, data) => {
      const tag = node.tagName.toLowerCase();
      const attr = data.attrName;
      const value = data.attrValue;

      // Check 1: To check for allowable attributes
      const isGlobalAttr = this.globalAttributes?.some((globalAttr) => {
        return globalAttr === attr;
      });

      const isAttributeAllowed = this.allowedAttributesMapping[tag]?.includes(attr);

      if (!isGlobalAttr && !isAttributeAllowed) {
        data.keepAttr = false;
        return;
      }

      // Check 2: To check for allowable values in an attribute
      const attributeValueMappingFunction =
        this.allowedAttributeValuesMappingFunction?.[tag]?.[attr];
      if (
        typeof attributeValueMappingFunction === "function" &&
        !attributeValueMappingFunction(value)
      ) {
        data.keepAttr = false;
        return;
      }
    });
  }

  sanitizeHtmlForRichText(unsanitizeHtml: string, configuration) {
    if (isEmpty(unsanitizeHtml) || isEmpty(configuration)) {
      return "";
    }

    this.globalAttributes = [];
    this.allowedAttributesMapping = {};
    this.allowedAttributeValuesMappingFunction = {};

    Object.keys(configuration).forEach((tag: string) => {
      const attributesString = configuration[tag].attributes;
      let attributeArray: string[] = [];

      if (attributesString) {
        attributeArray = attributesString.split(",");
      }

      if (tag === ":all") {
        this.globalAttributes = attributeArray;
      } else {
        this.allowedAttributesMapping[tag] = attributeArray;
      }

      const enforcedAttributesMapping = configuration[tag].enforcedAttributes;
      if (enforcedAttributesMapping) {
        Object.keys(enforcedAttributesMapping).forEach((attribute) => {
          const { matcher, values } = enforcedAttributesMapping[attribute];
          const allowedValuesArray = values.split(",");
          this.allowedAttributeValuesMappingFunction[tag] = {};
          switch (matcher) {
            case "starts_with":
              this.allowedAttributeValuesMappingFunction[tag][attribute] = (value: string) =>
                allowedValuesArray.some((allowedValue: string) => allowedValue.startsWith(value));
              break;
            case "exact":
              this.allowedAttributeValuesMappingFunction[tag][attribute] = (value: string) => {
                return allowedValuesArray.some((allowedValue: string) => allowedValue === value);
              };
              break;
          }
        });
      }
    });

    const allAllowedTags = Object.keys(this.allowedAttributesMapping);
    const allAllowedAttributes = Array.from(
      new Set([...this.globalAttributes, ...Object.values(this.allowedAttributesMapping).flat()]),
    ) as string[];

    DOMPurify.removeAllHooks();
    const sanitizedHtml = DOMPurify.sanitize(unsanitizeHtml, {
      ALLOWED_TAGS: allAllowedTags,
      ALLOWED_ATTR: allAllowedAttributes,
    });
    return sanitizedHtml;
  }

  sanitizeHtmlForHighlight(unsanitizeHtml: string) {
    this.allowedAttributesMapping = {
      span: ["class"],
    };

    this.allowedAttributeValuesMappingFunction = {
      span: {
        class: (value) => value === "match-text",
      },
    };

    const allAllowedTags = Object.keys(this.allowedAttributesMapping);
    const allAllowedAttributes = Array.from(
      new Set([...Object.values(this.allowedAttributesMapping).flat()]),
    ) as string[];

    DOMPurify.removeAllHooks();
    const sanitizedHtml = DOMPurify.sanitize(unsanitizeHtml, {
      ALLOWED_TAGS: allAllowedTags,
      ALLOWED_ATTR: allAllowedAttributes,
    });
    return sanitizedHtml;
  }

  sanitizeHtmlForNotification(unsanitizeHtml: string) {
    const sanitizedHtml = DOMPurify.sanitize(unsanitizeHtml, {
      ALLOWED_TAGS: ["b", "br"],
    });
    return sanitizedHtml;
  }
}

const htmlSanitizer = new HtmlSanitizer();
export default htmlSanitizer;
